<template>
  <div>
    <!-- SCOPE, RENDERED. Deliberately wider than the rest of this board — all
         requirement types, every status not in DONE_STATUSES, not the five
         types / six statuses hades_views.py mirrors from cerberus. A workload
         number whose scope is invisible is a number people argue about
         instead of acting on. Also states plainly that the customer pill,
         search, quick filters and declared filters above do not apply here —
         same precedent as the Engineering tab's customer filter, but spelled
         out on screen rather than left to a comment, because this tab hides
         those controls entirely rather than rendering them as silent no-ops.
         Covers the tenant reading below too — it is a breakdown INSIDE this
         wider-scoped payload, not a re-wiring of the pill above. -->
    <p v-if="deadlines" class="text-xs text-gray-600 mb-4 leading-relaxed">
      <span class="font-medium text-gray-800">Scope:</span> {{ deadlines.scope }}.
      Not filtered by customer, search or the filters above — this reads
      <code class="text-[11px]">GET /api/requirements/deadlines/</code>,
      a separate endpoint with a wider scope than the rest of this board.
      Generated {{ generatedLabel }}.
    </p>

    <LoadingIndicator v-if="loading" />
    <p v-else-if="error" class="text-sm text-red-dark py-4">{{ error }}</p>
    <template v-else-if="deadlines">
      <!-- OVERDUE / UNDATED / BEYOND — their own callouts, never folded into
           a week. Overdue bucketed into "this week" would make the week look
           worse than it is and hide that the work is already blown; Undated
           is the most alarming number in this payload and a bucketed view is
           exactly what hides it; Beyond exists so every open row lands in
           exactly one bucket and the totals reconcile. -->
      <div class="grid grid-cols-3 gap-4 mb-5">
        <!-- REAL <a>, not a click handler on a <div> — same reason
             Hades_Detail.vue's #2325 deeplink is an anchor: cmd-click,
             middle-click and copy-link all need a real href, and this tab's
             own goTo()-on-click predecessor was a same-tab $router.push that
             would have thrown away the board the reader is triaging from.
             `is` swaps the tag itself rather than layering an <a> INSIDE a
             <div> — Undated has no href and stays a plain <div>. -->
        <component
          :is="card.href ? 'a' : 'div'"
          v-for="card in calloutCards"
          :key="card.key"
          :href="card.href || undefined"
          :target="card.href ? '_blank' : undefined"
          :rel="card.href ? 'noopener' : undefined"
          class="border rounded-lg p-4 block"
          :class="[
            card.tone === 'danger' ? 'border-red-light bg-red-tint' : 'border-gray-200 bg-white',
            card.href ? 'hover:opacity-90' : '',
          ]"
        >
          <div
            class="text-xs font-semibold tracking-wide uppercase mb-1 flex items-center gap-1.5"
            :class="card.tone === 'danger' ? 'text-red-dark' : 'text-gray-600'"
          >
            {{ card.label }}
            <font-awesome-icon
              v-if="card.href"
              :icon="['fas', 'arrow-up-right-from-square']"
              class="text-[10px] opacity-60"
            />
          </div>
          <div class="flex items-baseline gap-2">
            <span
              class="text-2xl font-semibold leading-none"
              :class="card.tone === 'danger' ? 'text-red-dark' : 'text-gray-900'"
            >{{ card.bucket.count }}</span>
            <!-- THE SLIP NUMBER, GIVEN VISUAL WEIGHT, not buried in a column.
                 A bare count says nothing about whether the date means
                 anything — this is what turns it into a decision. -->
            <span v-if="card.bucket.slipped" class="text-xs font-medium text-red-dark">
              {{ card.bucket.slipped }} slipped
            </span>
          </div>
          <!-- DRAFT, SHOWN NOT SUBTRACTED. count already includes drafts;
               this says how many, in the same small/secondary register as
               "worst: moved N×" below rather than the red slipped tag —
               a draft is a caveat on the number, not a problem with it. -->
          <div v-if="card.bucket.draft" class="text-[11px] text-gray-500 mt-0.5">
            {{ card.bucket.draft }} draft
          </div>
          <div v-if="card.bucket.max_slips >= 2" class="text-[11px] text-red-dark mt-0.5">
            worst: moved {{ card.bucket.max_slips }}×
          </div>
        </component>
      </div>

      <!-- OVERALL — one row per week bucket, zero-filled across the horizon.
           A quiet week is a real answer, so every week renders even at 0. -->
      <section class="bg-white border border-gray-200 rounded-lg mb-5">
        <div class="px-5 py-3 border-b border-gray-200">
          <div class="text-sm font-semibold text-gray-900">
            Overall — {{ deadlines.horizon_weeks }}-week horizon
          </div>
        </div>
        <div class="overflow-auto" style="max-height: 320px">
          <table class="w-full text-[13px]" style="border-collapse: collapse; table-layout: fixed">
            <colgroup>
              <col style="width: 40%" />
              <col style="width: 20%" />
              <col style="width: 26%" />
              <col style="width: 14%" />
            </colgroup>
            <thead>
              <tr class="bg-gray-50 border-b border-gray-200 sticky top-0">
                <th class="pl-3 pr-2 py-2 text-left text-[11px] font-semibold tracking-wide uppercase text-gray-600">Week</th>
                <th class="px-2 py-2 text-left text-[11px] font-semibold tracking-wide uppercase text-gray-600">Due</th>
                <th class="px-2 py-2 text-left text-[11px] font-semibold tracking-wide uppercase text-gray-600">Slipped</th>
                <th class="px-2 pr-4 py-2 text-left text-[11px] font-semibold tracking-wide uppercase text-gray-600">Worst</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="week in deadlines.weeks"
                :key="week.week_start"
                class="border-b border-gray-100"
              >
                <td class="pl-3 pr-2 py-2 align-top text-gray-900 font-medium">
                  <!-- A <tr> cannot itself be an <a> — invalid table content
                       model, and browsers foster-parent it out of the table
                       entirely. The link lives on the week label instead,
                       same real-anchor/new-tab mechanics as the callouts
                       above. weekWorkLink always returns a link (never
                       null), so every row is one. -->
                  <a
                    :href="resolveHref(weekWorkLink(week.week_start))"
                    target="_blank"
                    rel="noopener"
                    class="inline-flex items-center gap-1 hover:underline hover:text-primary-700"
                  >
                    {{ weekLabel(week.week_start) }}
                    <font-awesome-icon
                      :icon="['fas', 'arrow-up-right-from-square']"
                      class="text-[10px] opacity-60"
                    />
                  </a>
                </td>
                <td class="px-2 py-2 align-top text-gray-800">
                  {{ week.count }}
                  <!-- Same caveat-not-headline treatment as the callouts. -->
                  <span v-if="week.draft" class="text-[11px] text-gray-500">
                    ({{ week.draft }} draft)
                  </span>
                </td>
                <td class="px-2 py-2 align-top">
                  <span v-if="week.slipped" class="text-red-dark font-medium">
                    {{ week.slipped }}
                  </span>
                  <span v-else class="text-gray-400">0</span>
                  <div v-if="week.slipped_twice_or_more" class="text-[11px] text-red-dark mt-0.5">
                    {{ week.slipped_twice_or_more }} moved ≥2×
                  </div>
                </td>
                <td class="px-2 pr-4 py-2 align-top">
                  <span v-if="week.max_slips >= 2" class="text-red-dark font-medium">{{ week.max_slips }}×</span>
                  <span v-else class="text-gray-400">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- BY ENGINEER and BY TENANT — two flat breakdowns of the same
           buckets, not a cross-tab (the payload has no owner+tenant grain,
           and deriving one client-side would be wrong rather than
           approximate — not attempted). Both render through
           Hades_BreakdownMatrix so neither invents its own layout; see that
           component for the shared table markup and the null-id linking
           rule each one supplies differently below. -->
      <div class="grid grid-cols-1 gap-5">
        <Hades_BreakdownMatrix
          :deadlines="deadlines"
          title="By engineer"
          subtitle="Overdue, each week, Beyond and Undated, split by owner. Total is deadlines.totals.by_owner — not summed here."
          row-header="Owner"
          breakdown-key="by_owner"
          id-field="owner_id"
          name-field="owner_name"
          query-param="owner"
        />
        <!-- NO nullQueryValue: not because the owner filter lacks an
             "unowned" concept (it has one — Support_FilterPopover's "No
             owner" option), but because parseQueryToFilters' numericFields
             runs `owner` through parseNum, which drops '__none__' as NaN.
             So Unowned (owner_id: null) stays plain text — never a link
             built on a value the URL would silently fail to carry. Full
             explanation in hades_deadlines.js's workLink comment. -->

        <Hades_BreakdownMatrix
          :deadlines="deadlines"
          title="By tenant"
          subtitle="Not filtered by the customer pill above — same wider scope as the rest of this tab. Overdue, each week, Beyond and Undated, split by tenant. Total is deadlines.totals.by_tenant — not summed here."
          row-header="Tenant"
          breakdown-key="by_tenant"
          id-field="tenant_id"
          name-field="tenant_name"
          query-param="tenant"
          null-query-value="__none__"
        />
        <!-- '__none__': parseQueryToFilters' tenant field has its own
             parseTenant branch that preserves the sentinel instead of
             running it through parseNum like owner's does, so Platform (the
             coalesced null-tenant row) is linkable, unlike Unowned above. -->
      </div>
    </template>
  </div>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import { formatDateValue } from '../support_constants';
import { beyondWorkLink, overdueWorkLink, weekWorkLink } from './hades_deadlines';
import Hades_BreakdownMatrix from './Hades_BreakdownMatrix.vue';

export default {
  name: 'HadesDeadlines',
  components: { LoadingIndicator, Hades_BreakdownMatrix },
  props: {
    deadlines: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    error: { type: String, default: null },
  },
  computed: {
    generatedLabel() {
      // generated_at is a full ISO INSTANT (Django timezone.now()), not a
      // bare calendar day like week_start — it goes through plain
      // toLocaleString(), not toLocalDate(), the same distinction metaLine
      // draws for run.completed_at.
      return new Date(this.deadlines.generated_at).toLocaleString();
    },
    calloutCards() {
      return [
        { key: 'overdue', label: 'Overdue', bucket: this.deadlines.overdue,
          tone: 'danger', href: this.resolveHref(overdueWorkLink()) },
        { key: 'undated', label: 'Undated', bucket: this.deadlines.undated,
          tone: 'neutral', href: null },
        { key: 'beyond', label: `Beyond ${this.deadlines.horizon_weeks}w`,
          bucket: this.deadlines.beyond, tone: 'neutral',
          href: this.resolveHref(beyondWorkLink(this.deadlines.weeks)) },
      ];
    },
  },
  methods: {
    weekWorkLink,
    weekLabel(weekStart) {
      return formatDateValue(weekStart, { month: 'short', day: 'numeric' });
    },
    // RESOLVED THROUGH $router, matching the mechanics #2325 established for
    // Hades_Detail's own deeplink — a real href a real <a> can carry, not a
    // hand-built string, so it stays correct if the route's path ever
    // changes. null in, null out: every call site above only renders an <a>
    // when this returns a string.
    resolveHref(link) {
      return link ? this.$router.resolve(link).href : null;
    },
  },
};
</script>
